import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { BanqueRow, CaisseRow, CompteBancaireRow, DeviseRow } from '@/lib/database.types';

export function useBanques() {
  return useQuery({
    queryKey: ['banques'],
    queryFn: async (): Promise<BanqueRow[]> => {
      const { data, error } = await supabase
        .from('banques')
        .select('*')
        .order('is_system', { ascending: false })
        .order('nom');
      if (error) throw error;
      return data;
    },
  });
}

export function useDevises() {
  return useQuery({
    queryKey: ['devises'],
    queryFn: async (): Promise<DeviseRow[]> => {
      const { data, error } = await supabase
        .from('devises')
        .select('*')
        .eq('is_active', true)
        .order('code');
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertBanqueInput {
  id?: string;
  code: string;
  nom: string;
  pays: string;
  swift?: string | null;
  is_active?: boolean;
}

export function useUpsertBanque() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertBanqueInput): Promise<BanqueRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('banques')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('banques').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['banques'] });
    },
  });
}

export function useDeleteBanque() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('banques').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['banques'] }),
  });
}

export function useComptesBancaires(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['comptes_bancaires', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<CompteBancaireRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('comptes_bancaires')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('intitule');
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertCompteInput {
  id?: string;
  entreprise_id: string;
  banque_id: string;
  intitule: string;
  numero_compte: string;
  rib?: string | null;
  iban?: string | null;
  devise_code: string;
  solde_initial: number;
  is_active?: boolean;
}

export function useUpsertCompte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertCompteInput): Promise<CompteBancaireRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('comptes_bancaires')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase
        .from('comptes_bancaires')
        .insert(rest)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['comptes_bancaires', row.entreprise_id] });
    },
  });
}

export function useBulkUpdateCompteActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; is_active: boolean }): Promise<void> => {
      const { error } = await supabase
        .from('comptes_bancaires')
        .update({ is_active: input.is_active })
        .in('id', input.ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comptes_bancaires'] }),
  });
}

export function useBulkUpdateCaisseActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { ids: string[]; is_active: boolean }): Promise<void> => {
      const { error } = await supabase
        .from('caisses')
        .update({ is_active: input.is_active })
        .in('id', input.ids);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['caisses'] }),
  });
}

export function useDeleteCompte() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('comptes_bancaires').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['comptes_bancaires'] }),
  });
}

export function useCaisses(entrepriseId: string | null) {
  return useQuery({
    queryKey: ['caisses', entrepriseId],
    enabled: !!entrepriseId,
    queryFn: async (): Promise<CaisseRow[]> => {
      if (!entrepriseId) return [];
      const { data, error } = await supabase
        .from('caisses')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('nom');
      if (error) throw error;
      return data;
    },
  });
}

export interface UpsertCaisseInput {
  id?: string;
  entreprise_id: string;
  nom: string;
  site?: string | null;
  devise_code: string;
  solde_initial: number;
  is_active?: boolean;
}

export function useUpsertCaisse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertCaisseInput): Promise<CaisseRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('caisses')
          .update(rest)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await supabase.from('caisses').insert(rest).select('*').single();
      if (error) throw error;
      return data;
    },
    onSuccess: (row) => {
      void qc.invalidateQueries({ queryKey: ['caisses', row.entreprise_id] });
    },
  });
}

export function useDeleteCaisse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('caisses').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['caisses'] }),
  });
}
